import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Container, Box, CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import ServerDashboard from './components/ServerDashboard';
import CreateServerDialog from './components/CreateServerDialog';
import ServerList from './components/ServerList';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import logo from './assests/logo.ico';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00E676', // Cyber Neon Green
      dark: '#00c853',
      light: '#69f0ae',
    },
    secondary: {
      main: '#00B0FF', // Neon Cyan
      dark: '#0091ea',
      light: '#40c4ff',
    },
    error: {
      main: '#FF1744', // Neon Red
    },
    warning: {
      main: '#FFD600', // Neon Yellow/Gold
    },
    background: {
      default: '#080810',
      paper: 'rgba(20, 20, 30, 0.65)',
    },
    text: {
      primary: '#eceef5',
      secondary: '#9095a5',
    }
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    h1: { fontFamily: 'Outfit, sans-serif', fontWeight: 800 },
    h2: { fontFamily: 'Outfit, sans-serif', fontWeight: 800 },
    h3: { fontFamily: 'Outfit, sans-serif', fontWeight: 800 },
    h4: { fontFamily: 'Outfit, sans-serif', fontWeight: 800, letterSpacing: '-0.5px' },
    h5: { fontFamily: 'Outfit, sans-serif', fontWeight: 700 },
    h6: { fontFamily: 'Outfit, sans-serif', fontWeight: 700, letterSpacing: '0.2px' },
    subtitle1: { fontFamily: 'Inter, sans-serif', fontWeight: 500 },
    body1: { fontFamily: 'Inter, sans-serif', lineHeight: 1.6 },
    body2: { fontFamily: 'Inter, sans-serif', lineHeight: 1.6 },
    button: { fontFamily: 'Outfit, sans-serif', fontWeight: 700, letterSpacing: '0.5px' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundImage: 'radial-gradient(circle at 10% 20%, rgba(0, 230, 118, 0.04) 0%, transparent 40%), radial-gradient(circle at 90% 80%, rgba(0, 176, 255, 0.04) 0%, transparent 40%), linear-gradient(135deg, #05050c 0%, #0c0d1b 50%, #040409 100%)',
          backgroundAttachment: 'fixed',
          minHeight: '100vh',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0,0,0,0.2)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(255,255,255,0.2)',
            }
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          padding: '8px 18px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          fontWeight: 700,
        },
        containedPrimary: {
          '&:hover': {
            boxShadow: '0 0 16px rgba(0, 230, 118, 0.4)',
          }
        },
        containedSecondary: {
          '&:hover': {
            boxShadow: '0 0 16px rgba(0, 176, 255, 0.4)',
          }
        }
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 16,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'rgba(22, 22, 35, 0.4)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
          borderRadius: 16,
          backgroundImage: 'none',
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 700,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(5, 5, 12, 0.5)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: 'rgba(15, 15, 25, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 20,
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
        }
      }
    }
  },
});

function Home() {
  return (
    <Container maxWidth="lg" sx={{ mt: 5, mb: 5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5 }}>
        <Typography variant="h4" component="h1" sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          fontFamily: 'Outfit, sans-serif',
          fontWeight: '900',
          background: 'linear-gradient(45deg, #00E676 30%, #00B0FF 90%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          filter: 'drop-shadow(0 2px 10px rgba(0,230,118,0.15))'
        }}>
          <SportsEsportsIcon sx={{ color: '#00E676', filter: 'drop-shadow(0 0 8px rgba(0,230,118,0.5))', fontSize: '2.5rem' }} />
          Active Server Instances
        </Typography>
        <CreateServerDialog />
      </Box>
      <ServerList />
    </Container>
  );
}

function App() {
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Router>
        <Box sx={{ flexGrow: 1, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <AppBar position="sticky" elevation={0}>
            <Container maxWidth="lg">
              <Toolbar disableGutters>
                <Typography variant="h6" component="div" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Link to="/" style={{ color: 'inherit', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <img src={logo} alt="logo" height="36" style={{ filter: 'drop-shadow(0 0 6px rgba(0,230,118,0.4))' }} />
                    <Box component="span" sx={{
                      fontFamily: 'Outfit, sans-serif',
                      fontWeight: 800,
                      fontSize: '1.4rem',
                      letterSpacing: '0.5px',
                      background: 'linear-gradient(90deg, #fff 0%, #b0b5c5 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}>
                      Goku MC Manager
                    </Box>
                  </Link>
                </Typography>
              </Toolbar>
            </Container>
          </AppBar>

          <Box sx={{ flexGrow: 1 }}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/server/:serverName" element={<ServerDashboard />} />
            </Routes>
          </Box>
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;